import React from 'react'
import {
  SingleDatePickerInput,
  TextInput,
  CheckboxInput,
} from '../../../global/components/forms/index.js'
import { DateTime } from 'luxon'
import UserInput from '../../../global/components/forms/UsersInput.jsx'
import { Info } from 'lucide-react'
import { Button } from '../../../global/components/ui/button.jsx'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../global/components/ui/tooltip.jsx'

export const RequestFilesTaskSettings = ({
  selectedRequestTemplate,
  templateDocuments,
  selectedDocuments,
  specialInstructions,
  dateRange,
  otherInputs,
  perDocumentDates,
  perOtherInputDates,
  dueDate,
  assignees,
  clientUserListItems,
  selectedClient,
  actions,
}) => {
  const handleUserChange = (e, index) => {
    const userId = e.target.value
    const user = clientUserListItems.find(u => Number(u._id) === Number(userId))

    if (user && user._id) {
      const assignee = {
        _id: user._id,
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        username: user.username || '',
        createContact: false,
      }
      actions.updateAssignee(index, assignee)
    }
  }

  const handleUserFormChange = e => {
    const { name, value } = e.target

    // Match pattern like: assignees[0]['firstname']
    const match = name.match(/(\w+)\[(\d+)\]\['(\w+)'\]/)
    if (!match) return

    const [, fieldName, index, subField] = match

    // Get current assignee and update only the changed field
    actions.updateAssigneeField(Number(index), subField, value)
  }

  const handleRemoveUser = index => {
    actions.removeAssignee(index)
  }

  return (
    <>
      {/* Date Range Section */}
      {selectedRequestTemplate.settings.date_range_enabled && (
        <>
          <div className="-setting yt-row space-between">
            <div className="-instructions yt-col">
              <p>
                <strong>Specify Ranges for Documents</strong>
              </p>
            </div>
            <div
              className="-inputs yt-col"
              style={{ display: 'flex', gap: '10px' }}
            >
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#666',
                  }}
                >
                  From
                </label>
                <SingleDatePickerInput
                  change={e => actions.setDateRange('from', e.target.value)}
                  name="dateRange.from"
                  placeholder="Pick a date"
                  initialDate={dateRange.from}
                  inputClasses="-right"
                  numberOfMonths={1}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#666',
                  }}
                >
                  To
                </label>
                <SingleDatePickerInput
                  change={e => actions.setDateRange('to', e.target.value)}
                  name="dateRange.to"
                  placeholder="Pick a date"
                  initialDate={dateRange.to}
                  inputClasses="-right"
                  numberOfMonths={1}
                />
              </div>
            </div>
          </div>
          <hr />
        </>
      )}

      {/* Due Date - Only show if enabled in template settings */}
      {selectedRequestTemplate.settings.due_date_enabled !== false && (
        <>
          <div className="-setting yt-row space-between">
            <div className="-instructions yt-col">
              <p>
                <strong>Due Date</strong>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="tw-ml-1 tw-h-6 tw-w-6 tw-align-middle"
                      aria-label="About the due date"
                    >
                      <Info aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    Select an optional due date. Upon due date expiration, the
                    request link is deactivated.
                  </TooltipContent>
                </Tooltip>
              </p>
            </div>
            <div className="-inputs yt-col">
              <SingleDatePickerInput
                anchorDirection="right"
                change={e => actions.setDueDate(e.target.value)}
                enableOutsideDays={false}
                initialDate={dueDate}
                inputClasses="-right"
                minDate={DateTime.local().toMillis()}
                name="dueDate"
                numberOfMonths={1}
                placeholder="Set due date"
              />
            </div>
          </div>
          <hr />
        </>
      )}

      {/* Select Files to Request */}
      {((templateDocuments && templateDocuments.length > 0) ||
        selectedRequestTemplate.settings.other_input_enabled) && (
        <>
          <div className="-setting yt-row space-between">
            <div className="-instructions yt-col">
              <p>
                <strong>
                  Select Files to Request
                  {templateDocuments && templateDocuments.length > 0 && (
                    <sup className="-required">*</sup>
                  )}
                </strong>
              </p>
            </div>
            <div className="-inputs yt-col">
              {templateDocuments && templateDocuments.length > 0 ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '8px',
                  }}
                >
                  {templateDocuments.map(doc => (
                    <div key={doc.id}>
                      <CheckboxInput
                        label={doc.name}
                        name={`document-${doc.id}`}
                        value={selectedDocuments.includes(doc.id)}
                        checked={selectedDocuments.includes(doc.id)}
                        change={() => actions.toggleDocumentSelection(doc.id)}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontStyle: 'italic', color: '#666' }}>
                  No predefined documents available for this template.
                </p>
              )}
            </div>
          </div>

          {/* Other Input - Add custom document names */}
          {selectedRequestTemplate.settings.other_input_enabled && (
            <div
              className="-setting yt-row space-between"
              style={{ marginTop: '16px' }}
            >
              <div className="-instructions yt-col">
                <p>
                  <strong>Add Other Documents</strong>
                </p>
              </div>
              <div className="-inputs yt-col">
                <div className="input-group">
                  {otherInputs.map((input, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <TextInput
                        change={e =>
                          actions.updateOtherInput(index, e.target.value)
                        }
                        name={`otherInput-${index}`}
                        placeholder="Enter custom document name"
                        value={input}
                        style={{ flex: 1 }}
                      />
                      {otherInputs.length > 1 && (
                        <button
                          type="button"
                          className="yt-btn x-small danger"
                          onClick={() => actions.removeOtherInput(index)}
                          style={{
                            padding: '6px 8px',
                            minWidth: 'auto',
                          }}
                        >
                          <i className="fal fa-trash" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="yt-btn xx-small"
                    onClick={() => actions.addOtherInput()}
                    style={{ marginTop: '8px' }}
                  >
                    <i className="fal fa-plus" /> Add another document
                  </button>
                </div>
              </div>
            </div>
          )}
          <hr />
        </>
      )}

      {/* Per Document Dates */}
      {selectedRequestTemplate.settings.per_document_dates &&
        (selectedDocuments.length > 0 ||
          otherInputs.some(input => input && input.trim())) && (
          <>
            <div className="-setting yt-row space-between">
              <div className="-instructions yt-col">
                <p>
                  <strong>Document Specific Dates</strong>
                </p>
              </div>
              <div className="-inputs yt-col">
                {/* Template Documents */}
                {selectedDocuments.map(docId => {
                  const doc = templateDocuments.find(d => d.id === docId)
                  if (!doc) return null

                  return (
                    <div
                      key={docId}
                      style={{ marginBottom: '10px' }}
                    >
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#666',
                        }}
                      >
                        {doc.name}
                      </label>
                      <div
                        className="-inputs yt-col"
                        style={{ display: 'flex', gap: '10px' }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#666',
                            }}
                          >
                            From
                          </label>
                          <SingleDatePickerInput
                            change={e =>
                              actions.setPerDocumentDate(
                                docId,
                                'from',
                                e.target.value,
                              )
                            }
                            name={`perDocumentDate-${docId}-from`}
                            placeholder="Pick a date"
                            initialDate={perDocumentDates[docId]?.from || null}
                            inputClasses="-right"
                            numberOfMonths={1}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#666',
                            }}
                          >
                            To
                          </label>
                          <SingleDatePickerInput
                            change={e =>
                              actions.setPerDocumentDate(
                                docId,
                                'to',
                                e.target.value,
                              )
                            }
                            name={`perDocumentDate-${docId}-to`}
                            placeholder="Pick a date"
                            initialDate={perDocumentDates[docId]?.to || null}
                            inputClasses="-right"
                            numberOfMonths={1}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}

                {/* Other Input Documents */}
                {otherInputs.map((input, index) => {
                  if (!input || !input.trim()) return null

                  return (
                    <div
                      key={index}
                      style={{ marginBottom: '10px' }}
                    >
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#666',
                        }}
                      >
                        {input.trim()}
                      </label>
                      <div
                        className="-inputs yt-col"
                        style={{ display: 'flex', gap: '10px' }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#666',
                            }}
                          >
                            From
                          </label>
                          <SingleDatePickerInput
                            change={e =>
                              actions.setPerOtherInputDate(
                                index,
                                'from',
                                e.target.value,
                              )
                            }
                            name={`perOtherInputDate-${index}-from`}
                            placeholder="Pick a date"
                            initialDate={
                              perOtherInputDates[index]?.from || null
                            }
                            inputClasses="-right"
                            numberOfMonths={1}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#666',
                            }}
                          >
                            To
                          </label>
                          <SingleDatePickerInput
                            change={e =>
                              actions.setPerOtherInputDate(
                                index,
                                'to',
                                e.target.value,
                              )
                            }
                            name={`perOtherInputDate-${index}-to`}
                            placeholder="Pick a date"
                            initialDate={perOtherInputDates[index]?.to || null}
                            inputClasses="-right"
                            numberOfMonths={1}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            <hr />
          </>
        )}

      {/* Special Instructions */}
      <div className="-setting yt-row space-between">
        <div className="-instructions yt-col">
          <p>
            <strong>Special Instructions</strong>
          </p>
        </div>
        <div className="-inputs yt-col">
          <textarea
            value={specialInstructions}
            onChange={e => actions.setSpecialInstructions(e.target.value)}
            placeholder="Include number of documents, specify if it needs to be signed, give specific dates or years, etc"
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              fontFamily: 'inherit',
              lineHeight: '1.4',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>
      <hr />

      {/* Assignees */}
      <div className="-setting yt-row space-between">
        <div className="-instructions yt-col">
          <p>
            <strong>Assignee</strong>
          </p>
        </div>
        <div className="yt-col">
          <button
            className="yt-btn xx-small u-pullRight input-group"
            onClick={() => actions.addAssignee()}
          >
            <i className="fal fa-plus" /> Add assignees
          </button>
          <div className="input-group">
            {assignees.map((assignee, i) => (
              <UserInput
                key={'assignee' + i}
                allowSharedEmail={true}
                change={handleUserFormChange}
                handleUserChange={e => handleUserChange(e, i)}
                currentIndex={i}
                userListItems={clientUserListItems}
                user={assignee}
                selectedClient={selectedClient || null}
                usersId={assignees.flatMap(item =>
                  item && item._id ? [Number(item._id)] : [],
                )}
                fieldName="assignees"
                users={assignees}
                removeUser={() => handleRemoveUser(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
